import React from 'react'
import { Link } from 'react-router-dom'
import { Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@emotion/react'
import { flagPath } from '../../../components/entities/CompaniesGrid'
import { normalizedName } from '../../../components/entities/CompanyName'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatPercentage,
} from '../../../packages/utils/numberFormatters'
import AdvisorList from '../../issues/AdvisorList'
import { useTranslation } from 'react-i18next'
import { FactsTable, FactsTableRow } from '../../components/FactsTable'
import { getConvertibleLoanAmount } from '../../../components/utils/convertibleUtils'

function ConvertibleBasicInfo({ convertible }: { convertible: ConvertibleEntity }) {
    const { i18n } = useTranslation()
    const theme = useTheme()
    const locale = i18n.language
    const tableRows = []

    const loanAmount = getConvertibleLoanAmount(convertible)

    tableRows.push(
        <FactsTableRow key={tableRows.length} label='Bolag'>
            <Link to={`/companies/${convertible.companyId}`}>
                <Stack
                    direction='row'
                    spacing={2}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    {convertible.companyCountryCode && (
                        <Tooltip title={convertible.companyCountryCode}>
                            <div style={{ display: 'inline-block' }}>
                                <img
                                    src={flagPath(convertible.companyCountryCode)}
                                    width={25}
                                    style={{ display: 'block' }}
                                />
                            </div>
                        </Tooltip>
                    )}
                    <Typography>{normalizedName(convertible.companyName)}</Typography>
                </Stack>
            </Link>
        </FactsTableRow>,
    )

    tableRows.push(
        <FactsTableRow key={tableRows.length} label='Namn'>
            {convertible.convertibleName}
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow label='Status'>
            <Stack
                direction='row'
                spacing={1}
                sx={{
                    alignItems: 'center',
                }}
            >
                <div
                    style={{
                        height: '10px',
                        width: '10px',
                        margin: 0,
                        backgroundColor: convertible.completed
                            ? theme.palette.entityStatus.complete
                            : theme.palette.entityStatus.incomplete,
                        borderRadius: '50%',
                        display: 'inline-block',
                    }}
                />
                <Typography>{convertible.completed ? 'Återbetalt lån' : 'Utestående lån'}</Typography>
            </Stack>
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow key={tableRows.length} label='Lånebelopp'>
            {loanAmount !== null ? formatBigMoney(loanAmount, { locale }) : '-'}
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow key={tableRows.length} label='Förfallodatum'>
            <Stack spacing={1}>
                <Typography>{formatDateYYYYMMDD(convertible.maturityDate)}</Typography>
                {convertible.convertAnytime ? (
                    <Typography>Konvertering kan ske när som helst fram till förfallodagen</Typography>
                ) : null}
            </Stack>
        </FactsTableRow>,
    )
    if (convertible.noncashConsideration) {
        tableRows.push(
            <FactsTableRow key={tableRows.length} label='Apportegendom'>
                {convertible.noncashConsideration}
            </FactsTableRow>,
        )
    }
    tableRows.push(
        <FactsTableRow
            key={tableRows.length}
            label={
                <Stack>
                    {convertible.arrangementFee !== null ? <Typography>Uppläggningsavgift</Typography> : null}
                    <Typography>
                        {convertible.monthlyInterest
                            ? 'Månadsränta'
                            : convertible.annualInterest
                              ? 'Årsränta'
                              : 'Ränta'}
                    </Typography>
                </Stack>
            }
        >
            <Stack>
                {convertible.arrangementFee !== null ? (
                    <Typography>{formatPercentage(convertible.arrangementFee, { locale, minDecimals: 0 })}</Typography>
                ) : null}
                <Typography>
                    {convertible.stibor ? 'STIBOR + ' : ''}
                    {convertible.monthlyInterest
                        ? formatPercentage(convertible.monthlyInterest, { locale, minDecimals: 0 })
                        : convertible.annualInterest
                          ? formatPercentage(convertible.annualInterest, { locale, minDecimals: 0 })
                          : 'Räntefritt'}
                </Typography>
            </Stack>
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow key={tableRows.length} label='Konverteringskurs'>
            {convertible.fixedConversionPrice ? (
                formatMoney(convertible.fixedConversionPrice, {
                    minDecimals: 2,
                    maxDecimals: 5,
                    locale,
                })
            ) : (
                <div>
                    <Typography variant='body1'>
                        {`VWAP-rabatt: ${
                            convertible.vwapDiscount
                                ? formatPercentage(convertible.vwapDiscount, {
                                      locale,
                                      minDecimals: 0,
                                  })
                                : '-'
                        }${convertible.vwapDays && convertible.vwapDays > 0 ? ` (${convertible.vwapDays} ${convertible.vwapDays > 1 ? 'dagar' : 'dag'})` : ''}`}
                    </Typography>
                    <Typography variant='body1'>
                        Min:{' '}
                        {convertible?.priceMin
                            ? formatMoney(convertible?.priceMin, {
                                  minDecimals: 2,
                                  maxDecimals: 5,
                                  locale,
                              })
                            : 'kvotvärde'}
                        , Max:{' '}
                        {convertible?.priceMax
                            ? formatMoney(convertible?.priceMax, {
                                  minDecimals: 2,
                                  maxDecimals: 5,
                                  locale,
                              })
                            : '-'}
                    </Typography>
                </div>
            )}
        </FactsTableRow>,
    )

    const financialAdvisorExists = convertible.advisors.find((advisor) => advisor.type === 'financial_advisor')
    const legalAdvisorExists = convertible.advisors.find((advisor) => advisor.type === 'legal_advisor')

    tableRows.push(
        <FactsTableRow key={tableRows.length} label='Finansiella rådgivare'>
            {financialAdvisorExists ? <AdvisorList advisors={convertible.advisors} type='financial_advisor' /> : '-'}
        </FactsTableRow>,
    )
    tableRows.push(
        <FactsTableRow key={tableRows.length} label='Legala rådgivare'>
            {legalAdvisorExists ? <AdvisorList advisors={convertible.advisors} type='legal_advisor' /> : '-'}
        </FactsTableRow>,
    )

    return <FactsTable rows={tableRows} />
}
export default ConvertibleBasicInfo
